import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PopupModal } from "react-calendly";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
	ArrowRight,
	Clock,
	ShieldCheck,
	Star,
	Truck,
	Check,
	Award,
	Heart,
	Home,
} from "lucide-react";
import { Rocket } from "lucide-react";
import PopupOffer from "../components/PopupOffer";
import Hero from "../components/Hero";

const HomeScreen = () => {
	const [open, setOpen] = useState(false);
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [show, setShow] = useState(false);
	const [force, setForce] = useState(false);
	const saleEndDate = new Date("2025-04-5");
	const [timeLeft, setTimeLeft] = useState({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
	});

	// Countdown timer effect
	useEffect(() => {
		const timer = setInterval(() => {
			// Calculate the time remaining
			const difference = saleEndDate - new Date();
			let time = {};
			console.log(saleEndDate);
			if (difference > 0) {
				time = {
					days: Math.floor(difference / (1000 * 60 * 60 * 24)),
					hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
					minutes: Math.floor((difference / 1000 / 60) % 60),
					seconds: Math.floor((difference / 1000) % 60),
				};
			} else {
				clearInterval(timer);
			}
			setTimeLeft(time);
		}, 1000);
		return () => clearInterval(timer);
	}, [timeLeft]);

	const bounceTransition = {
		duration: 0.4,
		ease: [0.68, -0.25, 0.265, 1.25],
	};

	const productShowcaseImages = [
		{
			title: "Simply Cellular Shades",
			url: "/images/newHome/shades/cellular.jpg",
			to: "/shop/info/simply-cellular",
			description: "Elegant design meets superior insulation",
			benefits: [
				"Energy savings up to 25%",
				"Several fabric options",
				"Child-safe designs",
			],
		},
		{
			url: "/images/newHome/shades/blackout_roller.jpg",
			title: "Roller Shades",
			to: "/shop/info/roller",
			description: "Perfect balance of style and functionality",
			benefits: [
				"Complete light control",
				"Easy one-handed operation",
				"5-year warranty",
			],
		},
		{
			url: "/images/newHome/shades/blackout_zebra.jpg",
			title: "Zebra Shades",
			to: "/shop/info/zebra",
			description: "Unique design meets superior light control",
			benefits: [
				"Adjustable privacy",
				"Modern minimalist look",
				"Premium fabrics",
			],
		},
		{
			url: "/images/shadeinfo/shades/sheer/sheer.jpg",
			title: "Sheer Shades",
			to: "/shop/info/sheer",
			description: "Transform harsh sunlight into gentle illumination",
			benefits: [
				"UV protection",
				"Soft diffused light",
				"Elegant layered look",
			],
		},
	];

	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentImageIndex((prev) => (prev + 1) % productShowcaseImages.length);
		}, 5000);
		return () => clearInterval(timer);
	}, []);

	return (
		<div className="overflow-x-hidden">
			<Helmet>
				<title>
					Premium Custom Window Blinds & Shades | Exclusive Limited-Time Offer
				</title>
				<meta
					name="description"
					content="Toronto-crafted premium window treatments. Get perfectly fitted blinds with free samples & consultation. Limited-time 50% savings on custom orders."
				/>
			</Helmet>
			<PopupOffer
				show={show}
				setShow={setShow}
				force={force}
				setForce={setForce}
			/>
			{/* Limited-Time Offer Banner */}
			<motion.div
				className="bg-gradient-to-r from-secondary to-primary py-3 text-center text-white"
				initial={{ y: -100 }}
				animate={{ y: 0 }}
				transition={bounceTransition}
			>
				<div className="container mx-auto px-4 flex flex-col md:flex-row justify-center items-center gap-2">
					<div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
						<Clock size={16} />
						<span className="font-medium">
							Offer ends in: {timeLeft.days}d {timeLeft.hours}h{" "}
							{timeLeft.minutes}m {timeLeft.seconds}s
						</span>
					</div>
					<p className="text-sm md:text-base font-medium">
						🎉 Exclusive: 50% Off Custom Window Treatments - Limited Stock
						Available
					</p>
				</div>
			</motion.div>
			<Hero />
			{/* Problem-Solution Section */}
			<section className="py-16 bg-gray-50">
				<div className="container mx-auto px-4">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={bounceTransition}
						className="text-center mb-16"
					>
						<h2 className="text-3xl md:text-4xl font-bold mb-6">
							Tired of Generic Window Coverings?
						</h2>
						<p className="text-xl text-gray-600 max-w-3xl mx-auto">
							Our precision-crafted custom shades solve the 6 biggest window
							treatment frustrations:
						</p>
					</motion.div>

					<div className="grid grid-cols-1 md:grid-cols-4 gap-8">
						{[
							{
								icon: <Star className="text-primary w-8 h-8" />,
								title: "Fully Customizable",
								description:
									"Choose from a number of fabrics, colours, and styles to create the perfect look for your space.",
								stat: "100%",
								statLabel: "Customer Satisfaction",
							},
							{
								icon: <Check className="text-primary w-8 h-8" />,
								title: "Perfect Fit Guarantee",
								description:
									"No more gaps or awkward measurements - we custom-make every shade to your exact window dimensions.",
								stat: "100%",
								statLabel: "Perfect Fit Rate",
							},
							{
								icon: <Award className="text-primary w-8 h-8" />,
								title: "Premium Quality",
								description:
									"Unlike big-box store options, our shades use commercial-grade materials that last for years.",
								stat: "5-Year",
								statLabel: "Warranty",
							},
							{
								icon: <Heart className="text-primary w-8 h-8" />,
								title: "Designer Looks",
								description:
									"Transform your windows from afterthoughts to focal points with our curated designer collections.",
								stat: "50+",
								statLabel: "Fabric Options",
							},
						].map((item, index) => (
							<motion.div
								key={index}
								className="flex flex-col justify-between bg-white shadow-md rounded-xl p-8 text-center hover:shadow-lg transition-all"
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ ...bounceTransition, delay: index * 0.1 }}
								viewport={{ once: true }}
							>
								<div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
									{item.icon}
								</div>
								<h3 className="text-2xl font-bold mb-4">{item.title}</h3>
								<p className="text-gray-600 mb-6">{item.description}</p>
								<div className="text-primary text-3xl font-bold">
									{item.stat}
								</div>
								<div className="text-gray-500 text-sm">{item.statLabel}</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>
			<section className="py-24 bg-white">
				<div className="container mx-auto px-4">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={bounceTransition}
						className="text-center mb-16"
					>
						<h2 className="text-3xl md:text-4xl font-bold mb-6">
							Why Choose Offcut Shades?
						</h2>
						<p className="text-xl text-gray-600 max-w-3xl mx-auto">
							We're more than just a window treatment company - we're a team of
							passionate designers and craftsmen dedicated to creating the
							perfect shades for your home.
						</p>
					</motion.div>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						{[
							{
								icon: <Rocket className="text-5xl text-primary" />,
								title: "Fast Turnaround",
								description: "Most custom orders ship within 2 business days",
								color: "bg-white",
								stat: "2 Days",
								statLabel: "Average Turnaround",
							},
							{
								icon: <ShieldCheck className="text-5xl text-primary" />,
								title: "Satisfaction Guarantee",
								description: "30-day love-it-or-return-it policy",
								color: "bg-white",
								stat: "30 Days",
								statLabel: "Return Policy",
							},
							{
								icon: <Truck className="text-5xl text-primary" />,
								title: "Free Shipping & Returns",
								description: "Free delivery and easy returns Canada-wide",
								color: "bg-white",
								stat: "Free",
								statLabel: "Across Canada",
							},
						].map((item, index) => (
							<motion.div
								key={index}
								className={`bg-white shadow-md rounded-xl p-8 text-center hover:shadow-lg transition-all`}
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ ...bounceTransition, delay: index * 0.1 }}
								viewport={{ once: true }}
							>
								<div className="bg-primary/10 w-16 h-16 rounded-full flex flex-col items-center justify-center mx-auto mb-6">
									{item.icon}
								</div>

								<h3 className="text-2xl font-bold mb-4">{item.title}</h3>
								<p className="text-gray-600 mb-6">{item.description}</p>
								<div className="text-primary text-3xl font-bold">
									{item.stat}
								</div>
								<div className="text-gray-500 text-sm">{item.statLabel}</div>
							</motion.div>
						))}
					</div>
				</div>
			</section>
			{/* Product Showcase */}
			<section className="px-4 md:px-0 overflow-hidden">
				<div className="p-10 container rounded-3xl bg-[#1A1A1A] mx-auto">
					<div className="grid md:grid-cols-2 gap-12 items-center">
						<motion.div
							className="text-white"
							initial={{ opacity: 0, x: -50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={bounceTransition}
						>
							<h2 className="text-4xl md:text-5xl font-bold mb-8">
								Our Best-Selling Window Treatments
							</h2>
							<AnimatePresence mode="wait">
								<motion.div
									key={productShowcaseImages[currentImageIndex].title}
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -20 }}
									transition={bounceTransition}
								>
									<h3 className="text-2xl font-semibold mb-4">
										{productShowcaseImages[currentImageIndex].title}
									</h3>
									<p className="text-white/90 mb-6">
										{productShowcaseImages[currentImageIndex].description}
									</p>

									<ul className="mb-8 space-y-2">
										{productShowcaseImages[currentImageIndex].benefits.map(
											(benefit, i) => (
												<li key={i} className="flex items-start">
													<Check className="text-primary mr-2 mt-1 flex-shrink-0" />
													<span>{benefit}</span>
												</li>
											)
										)}
									</ul>

									<div className="flex flex-col md:flex-row gap-12 md:gap-4">
										<motion.div
											whileHover={{ scale: 1.05 }}
											whileTap={{ scale: 0.95 }}
											className="inline-block"
										>
											<Link
												to={productShowcaseImages[currentImageIndex].to}
												className="px-8 py-4 bg-primary border-2 border-primary rounded-xl text-white font-semibold shadow-lg hover:bg-primary/90 transition-colors"
											>
												Shop Now
											</Link>
										</motion.div>
										<motion.div
											whileHover={{ scale: 1.05 }}
											whileTap={{ scale: 0.95 }}
											className="inline-block"
										>
											<Link
												to="/swatches"
												className="px-8 py-4 bg-transparent border-2 border-white rounded-xl text-white font-semibold hover:bg-white/10 transition-colors"
											>
												Order Samples
											</Link>
										</motion.div>
									</div>
								</motion.div>
							</AnimatePresence>
						</motion.div>

						<div className="relative aspect-[4/3] w-full">
							<AnimatePresence mode="wait">
								<motion.div
									key={currentImageIndex}
									className="absolute inset-0 rounded-3xl shadow-2xl overflow-hidden"
									initial={{ opacity: 0, scale: 1.1, x: 50 }}
									animate={{ opacity: 1, scale: 1, x: 0 }}
									exit={{ opacity: 0, scale: 0.9, x: -50 }}
									transition={bounceTransition}
								>
									<img
										src={productShowcaseImages[currentImageIndex].url}
										className="w-full h-full object-cover"
										alt={productShowcaseImages[currentImageIndex].title}
									/>
								</motion.div>
							</AnimatePresence>
						</div>
					</div>

					{/* Navigation dots */}
					<div className="flex justify-center gap-4 mt-12">
						{productShowcaseImages.map((_, index) => (
							<motion.button
								key={index}
								className={`w-4 h-4 rounded-full border-2 border-white transition-colors ${
									currentImageIndex === index
										? "bg-white/90"
										: "bg-transparent hover:bg-white/50"
								}`}
								onClick={() => setCurrentImageIndex(index)}
								whileHover={{ scale: 1.2 }}
								whileTap={{ scale: 0.9 }}
							/>
						))}
					</div>
				</div>
			</section>
			{/* Testimonials Section */}
			<section className="py-24 bg-white">
				<div className="container mx-auto px-4">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={bounceTransition}
						className="text-center mb-16"
					>
						<h2 className="text-3xl md:text-4xl font-bold mb-6">
							Trusted by Toronto Homeowners
						</h2>
						<p className="text-xl text-gray-600 max-w-3xl mx-auto">
							Don't just take our word for it - hear what our customers say
						</p>
					</motion.div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						{[
							{
								quote:
									"The cellular shades have transformed our living room. Perfect fit and the insulation has noticeably reduced our energy bills.",
								author: "Sarah K., Toronto",
								rating: 5,
							},
							{
								quote:
									"I was nervous about ordering custom blinds online, but the free samples and video consultation made it so easy. The quality exceeded my expectations.",
								author: "Michael T., Mississauga",
								rating: 5,
							},
							{
								quote:
									"Our zebra shades get compliments from every guest. The dual-layer fabric provides perfect light control throughout the day.",
								author: "Jennifer L., Oakville",
								rating: 5,
							},
						].map((testimonial, index) => (
							<motion.div
								key={index}
								className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-all"
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ ...bounceTransition, delay: index * 0.1 }}
								viewport={{ once: true }}
							>
								<div className="flex mb-4">
									{[...Array(5)].map((_, i) => (
										<Star
											key={i}
											className={`w-5 h-5 ${
												i < testimonial.rating
													? "text-primary fill-primary"
													: "text-gray-300"
											}`}
										/>
									))}
								</div>
								<p className="text-gray-700 italic mb-6">
									"{testimonial.quote}"
								</p>
								<p className="font-medium text-gray-900">
									{testimonial.author}
								</p>
							</motion.div>
						))}
					</div>
				</div>
			</section>
			{/* Craftsmanship Section */}
			<section className="py-24 bg-gray-50">
				<div className="container mx-auto px-4">
					<div className="text-center md:text-left grid md:grid-cols-2 gap-16 items-center">
						<motion.div
							initial={{ opacity: 0, x: -50 }}
							whileInView={{ opacity: 1, x: 0 }}
							transition={bounceTransition}
						>
							<h2 className="text-4xl md:text-5xl font-bold mb-8">
								Toronto-Crafted Excellence
							</h2>
							<p className="text-xl text-gray-600 mb-8">
								Each shade is meticulously crafted in our Toronto facility,
								combining traditional expertise with cutting-edge technology to
								create window treatments that exceed expectations.
							</p>

							<div className="mb-8 space-y-4">
								{[
									"Precision laser measurements for perfect fit",
									"Commercial-grade components for durability",
									"Hand-finished details on every shade",
									"Eco-friendly materials and processes",
								].map((item, i) => (
									<div key={i} className="flex items-start">
										<Check className="text-primary mr-3 mt-1 flex-shrink-0" />
										<span className="text-lg">{item}</span>
									</div>
								))}
							</div>

							<div className="flex flex-col sm:flex-row gap-4">
								<motion.button
									onClick={() => setOpen(true)}
									className="px-8 py-4 bg-primary rounded-xl text-white font-semibold shadow-lg hover:bg-primary/90 transition-colors"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									Book a Free Consultation
								</motion.button>
								<motion.button
									onClick={() => setForce(true)}
									className="px-8 py-4 bg-white border-2 border-primary text-primary rounded-xl font-semibold hover:bg-gray-50 transition-colors"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									Get Today's Offer
								</motion.button>
							</div>
						</motion.div>

						<motion.div
							className="relative"
							initial={{ opacity: 0, scale: 0.9, x: 50 }}
							whileInView={{ opacity: 1, scale: 1, x: 0 }}
							transition={bounceTransition}
						>
							<img
								src="/images/about/about-us.jpg"
								className="w-full rounded-3xl shadow-2xl"
								alt="Craftsmanship"
							/>
							<motion.div
								className="absolute -bottom-8 -left-2 md:-left-8 bg-white/90 backdrop-blur-sm text-black font-light p-6 rounded-2xl shadow-xl"
								initial={{ scale: 0, rotate: -10 }}
								whileInView={{ scale: 1, rotate: 0 }}
								transition={bounceTransition}
							>
								<p className="text-2xl font-normal">4.9/5</p>
								<p>Customer Rating</p>
							</motion.div>
						</motion.div>
					</div>
				</div>
			</section>
			{/* Sample Request CTA */}
			<section className="bg-black py-24 relative overflow-hidden">
				<div className="absolute inset-0 bg-gradient-to-r from-secondary to-primary skew-y-3 transform origin-top-right" />
				<div className="container mx-auto px-4 relative">
					<motion.div
						className="max-w-3xl mx-auto text-center text-white"
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={bounceTransition}
					>
						<h2 className="text-4xl md:text-5xl font-bold mb-8">
							Try Before You Buy
						</h2>
						<p className="text-xl mb-8 text-white/90">
							Request up to 5 free fabric samples to see and feel the quality in
							your home
						</p>
						<motion.div
							className="flex flex-col sm:flex-row justify-center gap-4"
							whileHover={{ scale: 1.02 }}
							whileTap={{ scale: 0.98 }}
						>
							<Link
								to="/swatches"
								className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary rounded-xl font-semibold shadow-lg group"
							>
								<span>Order Free Samples</span>
								<ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" />
							</Link>
							<button
								onClick={() => setOpen(true)}
								className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-colors"
							>
								<span>Need Help Choosing?</span>
								<ArrowRight className="ml-2" />
							</button>
						</motion.div>

						<div className="mt-8 text-sm text-white/80">
							<p>Over 1,000 samples shipped last month</p>
						</div>
						{/* Decorative elements */}
						<motion.div
							className="absolute -top-20 -left-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"
							animate={{
								scale: [1, 1.2, 1],
								rotate: [0, 180, 0],
							}}
							transition={{
								duration: 8,
								repeat: Infinity,
								ease: "linear",
							}}
						/>
						<motion.div
							className="absolute -bottom-20 -right-20 w-60 h-60 bg-white/10 rounded-full blur-3xl"
							animate={{
								scale: [1.2, 1, 1.2],
								rotate: [180, 0, 180],
							}}
							transition={{
								duration: 8,
								repeat: Infinity,
								ease: "linear",
							}}
						/>
					</motion.div>
				</div>
			</section>
			<PopupModal
				url="https://calendly.com/offcutshades"
				rootElement={document.getElementById("root")}
				open={open}
				onModalClose={() => setOpen(false)}
			/>
		</div>
	);
};

export default HomeScreen;
